import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { fonts, scaleFont } from "@theme";
import type { ReactNode } from "react";
import { StyleSheet } from "react-native";

interface GamificationSummaryCardsProps {
  elo: number;
  tierLabel: string;
  spendableGems: number;
  recentGemDelta: number;
  recentGemWindowDays: number;
  onEloPress?: () => void;
}

const ELO_ACCENT = "#FF9D3B";
const GEM_ACCENT = "#B56CFF";
const GEM_DELTA = "#58D29A";

const formatNumber = (value: number) =>
  Math.round(value).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatDelta = (value: number) => {
  const formatted = formatNumber(value);
  return value > 0 ? `+${formatted}` : formatted;
};

const SummaryCard = ({
  icon,
  label,
  value,
  detail,
  accentColor,
  detailColor = accentColor,
  backgroundColor,
  borderColor,
  onPress,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  accentColor: string;
  detailColor?: string;
  backgroundColor: string;
  borderColor: string;
  onPress?: () => void;
}) => {
  const card = (
    <LBox
      flex={1}
      borderRadius="l"
      padding="s4"
      justifyContent="space-between"
      minHeight={152}
      style={[
        styles.card,
        {
          backgroundColor,
          borderColor,
        },
      ]}
    >
      <LBox gap="s3">
        <LBox flexDirection="row" alignItems="center" gap="s1_5">
          {icon}
          <LText style={[styles.label, { color: "rgba(255,255,255,0.56)" }]}>
            {label}
          </LText>
        </LBox>

        <LText
          color="textPrimary"
          numberOfLines={1}
          adjustsFontSizeToFit
          style={styles.value}
        >
          {value}
        </LText>
      </LBox>

      <LText
        numberOfLines={2}
        adjustsFontSizeToFit
        style={[
          styles.detail,
          {
            color: detailColor,
          },
        ]}
      >
        {detail}
      </LText>
    </LBox>
  );

  return onPress ? (
    <LPressable
      accessibilityLabel="View fan levels"
      accessibilityRole="button"
      onPress={onPress}
      sentryEventName="profile-elo-levels"
      style={styles.pressableCard}
    >
      {card}
    </LPressable>
  ) : (
    card
  );
};

export const GamificationSummaryCards = ({
  elo,
  tierLabel,
  spendableGems,
  recentGemDelta,
  recentGemWindowDays,
  onEloPress,
}: GamificationSummaryCardsProps) => {
  const recentWindowLabel =
    recentGemWindowDays === 7
      ? "this week"
      : `last ${recentGemWindowDays} days`;

  return (
    <LBox flexDirection="row" gap="s3">
      <SummaryCard
        icon={
          <LIcon
            name="crown"
            colors={{ background: ELO_ACCENT, crown: ELO_ACCENT }}
            size={16}
          />
        }
        label="ELO"
        value={formatNumber(elo)}
        detail={tierLabel}
        accentColor={ELO_ACCENT}
        backgroundColor="rgba(47, 117, 230, 0.14)"
        borderColor="rgba(89, 150, 220, 0.24)"
        onPress={onEloPress}
      />
      <SummaryCard
        icon={<LIcon name="gem" size={16} />}
        label="GEMS"
        value={formatNumber(spendableGems)}
        detail={`${formatDelta(recentGemDelta)} ${recentWindowLabel}`}
        detailColor={GEM_DELTA}
        accentColor={GEM_ACCENT}
        backgroundColor="rgba(91, 46, 145, 0.18)"
        borderColor="rgba(181, 108, 255, 0.28)"
      />
    </LBox>
  );
};

export default GamificationSummaryCards;

const styles = StyleSheet.create({
  pressableCard: {
    flex: 1,
  },
  card: {
    borderWidth: 1,
  },
  detail: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(25),
  },
  label: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(17),
    letterSpacing: 0,
    lineHeight: scaleFont(22),
  },
  value: {
    fontFamily: fonts.extraBold,
    fontSize: scaleFont(32),
    lineHeight: scaleFont(38),
  },
});
